import { Goal, GoalContribution } from '../../services/goalApi';
import {
  averageMonthlyDeposit,
  futureValue,
  monthlyPercentFrom,
  monthsUntil,
  requiredMonthlyDeposit,
} from '../../utils/projections';

/** Ten years, the horizon the projection table already quotes. */
export const GROWTH_MONTHS = 120;
/** The table's rows, as months: 1, 5 and 10 years. */
export const GROWTH_MARKS = [12, 60, 120];
/** The pace an event goal runs at: deposits over the last N months. */
export const PACE_WINDOW_MONTHS = 3;

/** A habit's outlook: the balance month by month, with and without the quoted yield. */
export interface GrowthOutlook {
  kind: 'growth';
  principal: number;
  monthly: number;
  /** the quoted rate, converted to % a month */
  monthlyRate: number;
  hasYield: boolean;
  quotedRate: number;
  ratePeriod: 'monthly' | 'yearly';
  /** balances at months 0..GROWTH_MONTHS, no yield */
  flat: number[];
  /** same, at the quoted yield; null when no yield was quoted */
  grown: number[] | null;
}

/** An event goal's outlook: the pace it needs against the pace it has. */
export interface PathOutlook {
  kind: 'path';
  principal: number;
  target: number;
  monthsToDeadline: number;
  needed: number;
  average: number;
  behind: boolean;
  /** months until the target at the current pace; null without recent deposits */
  arrivalMonths: number | null;
}

export type GoalOutlook = GrowthOutlook | PathOutlook | null;

/**
 * What a goal's numbers become, for the chart and the facts beside it. Pure:
 * the same arithmetic the projections panel ran, only reshaped. Achieved and
 * archived goals have nothing to project; so has an event goal without a
 * deadline.
 */
export const goalOutlook = (goal: Goal, trail: GoalContribution[] = [], now: Date = new Date()): GoalOutlook => {
  if (goal.isAchieved || goal.status !== 'active') {
    return null;
  }

  const quotedRate = Number(goal.expectedGrowthRate ?? 0);
  const ratePeriod = goal.growthRatePeriod ?? 'yearly';
  const monthlyRate = monthlyPercentFrom(quotedRate, ratePeriod);
  const principal = Math.max(0, Number(goal.currentAmount) || 0);

  if (goal.goalType === 'recurring') {
    const monthly = Math.max(0, Number(goal.monthlyTarget ?? 0));
    if (monthly <= 0 && principal <= 0) {
      return null;
    }
    const series = (rate: number) =>
      Array.from({ length: GROWTH_MONTHS + 1 }, (_, month) => futureValue(principal, monthly, rate, month));
    return {
      kind: 'growth',
      principal,
      monthly,
      monthlyRate,
      hasYield: monthlyRate > 0,
      quotedRate,
      ratePeriod,
      flat: series(0),
      grown: monthlyRate > 0 ? series(monthlyRate) : null,
    };
  }

  if (!goal.targetDate) {
    return null;
  }
  const monthsToDeadline = monthsUntil(goal.targetDate, now);
  if (monthsToDeadline <= 0) {
    return null;
  }
  const target = Number(goal.targetAmount ?? 0);
  const needed = requiredMonthlyDeposit(target, principal, monthlyRate, monthsToDeadline);
  if (needed == null) {
    return null;
  }
  const { average, arrivalMonths } = eventPace(goal, trail, now);

  return { kind: 'path', principal, target, monthsToDeadline, needed, average, behind: average < needed, arrivalMonths };
};

/**
 * Any event goal's pace, deadline or not: the average deposit over the last
 * months and, from it, how many months the rest takes.
 */
export const eventPace = (
  goal: Goal,
  trail: GoalContribution[] = [],
  now: Date = new Date(),
): { average: number; remaining: number; arrivalMonths: number | null } => {
  const principal = Math.max(0, Number(goal.currentAmount) || 0);
  const target = Number(goal.targetAmount ?? 0);
  const average = averageMonthlyDeposit(
    trail.map((entry) => ({ amount: entry.amount, date: entry.date, kind: entry.kind })),
    PACE_WINDOW_MONTHS,
    now,
  );
  const remaining = Math.max(target - principal, 0);
  const arrivalMonths = remaining === 0 ? 0 : average > 0 ? remaining / average : null;
  return { average, remaining, arrivalMonths };
};

/** The first day of the month `months` from now, for "chega em março de 2027". */
export const monthsFromNow = (months: number, now: Date = new Date()): Date =>
  new Date(now.getFullYear(), now.getMonth() + Math.ceil(months), 1);
